import { Theme } from '@mui/material';
import { globalClasses } from 'core/common/constants/global-classes';
import { CheckboxVariant } from 'core/flow/entities';

declare module '@mui/material/FormControlLabel' {
  interface FormControlLabelProps {
    variant?: CheckboxVariant;
  }
}

export const getFormControlLabelOptions = ({ palette, breakpoints }: Theme) => {
  const formControlLabelComponents = {
    styleOverrides: {
      root: ({
        ownerState,
      }: {
        ownerState: { variant?: CheckboxVariant; checked: boolean; srcImage?: string };
      }) => {
        const baseStyles = {
          marginLeft: 0,
          marginRight: 0,
          color: palette.button.contrastOptionText,
          borderRadius: '16px',
          textTransform: 'none',
          display: 'flex',
          justifyContent: 'flex-start',
          minHeight: '56px',
          boxShadow: palette.button.optionShadow,
          padding: '16px 12px',
          height: '100%',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            border: `1px solid ${palette.button.optionHover}`,
            color: palette.button.contrastOptionText,
            backgroundColor: palette.button.option,
          },
          '&:disabled': {
            backgroundColor: palette.button.optionDisabled,
            color: palette.button.contrastOptionText,
          },
          [`& ${globalClasses.typographyRoot}`]: {
            width: '100%',
            display: 'flex',
            alignItems: 'center',
          },
          [`& ${globalClasses.checkboxRoot}`]: {
            padding: 0,
          },
          [`& ${globalClasses.boxRoot}`]: {
            fontSize: '16px',
            [breakpoints.up('mobile')]: {
              fontSize: '18px',
            },
          },
        };
        const checkedStateStyles = {
          backgroundColor: ownerState.checked
            ? palette.button.optionPressed
            : palette.button.option,
          border: `1px solid ${ownerState.checked ? 'transparent' : palette.button.option}`,
        };
        const imageVariantStyles = {
          backgroundImage: ownerState.variant === 'image' && `url(${ownerState.srcImage})`,
        };
        return {
          ...baseStyles,
          ...checkedStateStyles,
          ...imageVariantStyles,
        };
      },
    },
    //TODO reset last item margin
    variants: [
      {
        props: { variant: 'tile' },
        style: {
          position: 'relative',
          flexDirection: 'row',
          alignItems: 'flex-start',
          marginBottom: '16px',

          [`& ${globalClasses.typographyRoot}`]: {
            flexDirection: 'column',
            alignItems: 'center',
          },
          [`& ${globalClasses.checkboxRoot}`]: {
            position: 'absolute',
            top: '5px',
            right: '5px',
          },
          img: {
            width: '44px',
            height: '44px',
            marginBottom: '8px',
          },
        },
      },
      {
        props: { variant: 'image' },
        style: {
          position: 'relative',
          flexDirection: 'row',
          alignItems: 'flex-start',
          height: '108px',
          marginBottom: '8px',

          [`& ${globalClasses.typographyRoot}`]: {
            marginBottom: '16px',
          },
          [`& ${globalClasses.checkboxRoot}`]: {
            position: 'absolute',
            top: '5px',
            right: '5px',
          },
          img: {
            width: '44px',
            height: '44px',
            marginBottom: '8px',
          },
        },
      },
      {
        props: { variant: 'option' },
        style: {
          alignItems: 'center',
          marginBottom: '16px',

          img: {
            marginRight: '8px',
          },
        },
      },
      {
        props: { variant: 'pill' },
        style: {
          alignItems: 'center',
          marginBottom: '12px',
          marginRight: '12px',
          borderRadius: '40px',
          padding: '8px 12px',
          minHeight: 'auto',
          [breakpoints.up('mobile')]: {
            padding: '10px 16px',
            marginBottom: '16px',
            marginRight: '16px',
          },
          [`& ${globalClasses.checkboxRoot}`]: {
            position: 'absolute',
            height: '0px',
            width: '0px',
          },
          [`& ${globalClasses.svgIconRoot}`]: {
            position: 'absolute',
            height: '0px',
            width: '0px',
          },
          img: {
            marginRight: '8px',
          },
        },
      },
    ],
  };

  return { formControlLabelComponents };
};
